import { datadogRum } from '@datadog/browser-rum';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router';
import './antd.css';
import { configureI18Next } from './configureI18Next';
import configureZod from './configureZod';
import {
    AGENT_API_URL,
    API_HOST,
    ENVIRONMENT,
    IS_LIVE_ENVIRONMENT,
    RELEASE_VERSION,
    SENTRY_DSN,
} from './constants/environment';
import './index.css';
import { App } from './routes/App';

const FAILED_FETCH_WITH_HOST = /^Failed to fetch \(([^)]+)\)$/i;
// Suppress only known third-party fetch-noise hosts blocked by browser/privacy tooling.
// Keep this list intentionally narrow so real app/network failures still reach Sentry.
const NOISY_FETCH_HOST_PATTERNS = [/^cdn\.segment\.com$/i, /^browser-intake-datadoghq\./i];

function isConsoleOriginEvent(event: Sentry.Event) {
    if (event.logger === 'console') {
        return true;
    }

    const exceptionValues = event.exception?.values ?? [];
    return exceptionValues.some((value) => value.mechanism?.type === 'console');
}

function getEventMessage(event: Sentry.Event, hint?: Sentry.EventHint) {
    return event.message ?? event.logentry?.message ?? hint?.originalException?.toString?.() ?? '';
}

function shouldDropSentryEvent(event: Sentry.Event, hint?: Sentry.EventHint) {
    if (!isConsoleOriginEvent(event)) {
        return false;
    }

    const match = FAILED_FETCH_WITH_HOST.exec(getEventMessage(event, hint));
    if (!match) {
        return false;
    }

    const host = match[1]?.trim() ?? '';
    return NOISY_FETCH_HOST_PATTERNS.some((pattern) => pattern.test(host));
}

datadogRum.init({
    applicationId: '15baf5ce-527c-4fca-a5bf-317cb8836801',
    clientToken: 'pub377b7334467bf34f1fd9bb0d1a2072a4',
    site: 'datadoghq.com',
    service: 'web-app',
    env: ENVIRONMENT,
    version: RELEASE_VERSION,
    useSecureSessionCookie: true,
    trackSessionAcrossSubdomains: true,
    sessionSampleRate: ENVIRONMENT == 'staging' ? 100 : 0, // test in staging first
    sessionReplaySampleRate: 0, // Disable Session Replay
    trackUserInteractions: ENVIRONMENT == 'staging', // test in staging first
});

const initSentry = () => {
    const tracePropagationTargets = Array.from(
        new Set(
            [window.location.origin, API_HOST, AGENT_API_URL].filter((value): value is string =>
                Boolean(value),
            ),
        ),
    );
    const tracesSampleRate = ENVIRONMENT === 'production' ? 0.2 : 1.0;

    Sentry.init({
        release: RELEASE_VERSION,
        dsn: SENTRY_DSN,
        environment: ENVIRONMENT,
        debug: false,
        enabled: IS_LIVE_ENVIRONMENT,
        sendDefaultPii: false,
        tracesSampleRate,
        tracePropagationTargets,
        integrations: [
            Sentry.reactRouterV7BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            Sentry.captureConsoleIntegration({
                levels: ['error'],
            }),
        ],
        beforeSend(event, hint) {
            if (shouldDropSentryEvent(event, hint)) {
                return null;
            }
            return event;
        },
    });
};

const renderApp = () => {
    const container = document.getElementById('root');
    const root = createRoot(container!);
    root.render(
        <Sentry.ErrorBoundary fallback={<div>Something went wrong.</div>}>
            <App />
        </Sentry.ErrorBoundary>,
    );
};

const bootstrap = async () => {
    // Configure i18n first, then Zod (Zod's error map uses i18n.t()).
    await configureI18Next();
    configureZod();

    try {
        initSentry();
    } catch (error) {
        console.error('Sentry was not set up correctly', error);
    }

    renderApp();
};

void bootstrap();
